
import React, { useState, useEffect } from 'react';
import { Page } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import ImageWithFallback from './ImageWithFallback';

interface HeaderProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  onSearch: (term: string) => void;
}

// Logo Mặc định (Static)
const LOGO_URL = "/logo.png";

const Header: React.FC<HeaderProps> = React.memo(({ currentPage, onNavigate, onSearch }) => {
  const { language, setLanguage, t } = useLanguage();
  const { itemCount, setCartOpen } = useCart();
  const { settings } = useSettings();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll to add shadow - Optimized
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('home'), value: Page.HOME },
    { label: t('shop'), value: Page.SHOP },
    { label: t('about'), value: Page.ABOUT },
    { label: t('blog'), value: Page.BLOG },
    { label: t('contact'), value: Page.CONTACT },
  ];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      onSearch(searchTerm);
      setIsSearchOpen(false);
      setSearchTerm('');
    }
  };

  const handleNavClick = (page: Page) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
  };

  const Logo = () => (
    <div
      className="flex items-center gap-3 cursor-pointer group select-none"
      onClick={() => handleNavClick(Page.HOME)}
    >
      <div className="relative">
        <ImageWithFallback
          src={settings?.branding?.logo_url || LOGO_URL}
          alt="Squeaky Town Logo"
          className="h-12 sm:h-14 w-auto object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-110 group-active:scale-95"
          style={{ imageRendering: 'crisp-edges', WebkitFontSmoothing: 'antialiased' } as React.CSSProperties}
          fallbackSrc={LOGO_URL}
        />
      </div>

      <h1 className="font-display text-xl sm:text-2xl font-black text-primary tracking-tighter group-hover:text-accent transition-colors">
        Squeaky Town
      </h1>
    </div>
  );

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${scrolled
          ? 'bg-[#F9F7F2]/95 backdrop-blur-md border-sage-light shadow-soft py-2'
          : 'bg-[#F9F7F2] border-transparent py-4'
          }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Logo />

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1 bg-white/50 p-1.5 rounded-full border border-sage-light/50">
              {navItems.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleNavClick(item.value)}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all relative ${currentPage === item.value
                    ? 'bg-primary text-white shadow-md'
                    : 'text-earth/70 hover:bg-white hover:text-primary'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Language Switcher */}
              <div className="hidden sm:flex items-center gap-1 bg-sage-light/50 p-1 rounded-full text-[11px] font-bold">
                <button
                  className={`px-2 py-1 rounded-full transition-colors ${language === 'vi' ? 'bg-white text-primary shadow-sm' : 'text-gray-400 hover:text-earth'}`}
                  onClick={() => setLanguage('vi')}
                >
                  VN
                </button>
                <button
                  className={`px-2 py-1 rounded-full transition-colors ${language === 'en' ? 'bg-white text-primary shadow-sm' : 'text-gray-400 hover:text-earth'}`}
                  onClick={() => setLanguage('en')}
                >
                  EN
                </button>
              </div>

              {/* Cart Button */}
              <button
                onClick={() => setCartOpen(true)}
                className="relative size-10 rounded-full bg-white border border-sage-light text-earth hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                aria-label="Cart"
                data-testid="cart-button"
              >
                <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
                {itemCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 size-5 bg-accent text-white text-[10px] font-black rounded-full flex items-center justify-center animate-in zoom-in"
                    data-testid="cart-count"
                  >
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>

              {/* Search Toggle */}
              <div className="relative">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`size-10 rounded-full transition-all flex items-center justify-center ${isSearchOpen ? 'bg-accent text-white shadow-glow' : 'bg-white border border-sage-light text-earth hover:border-accent hover:text-accent'}`}
                  aria-label="Search"
                >
                  <span className="material-symbols-outlined text-[20px]">search</span>
                </button>

                {isSearchOpen && (
                  <div className="absolute right-0 top-full mt-3 w-80 bg-white shadow-premium rounded-3xl border border-sage-light p-4 animate-in fade-in slide-in-from-top-2 duration-200 z-[60]">
                    <form onSubmit={handleSearchSubmit} className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        className="w-full text-sm bg-background-light border-none rounded-2xl px-4 py-3 focus:ring-2 focus:ring-accent/20 focus:text-earth placeholder:text-gray-400 transition-all font-medium"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="bg-accent text-white size-11 rounded-2xl hover:bg-primary shadow-lg shadow-accent/20 transition-all flex items-center justify-center">
                        <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                      </button>
                    </form>
                  </div>
                )}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden size-10 rounded-full bg-white border border-sage-light text-earth hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
              >
                <span className="material-symbols-outlined text-[24px]">menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Sidebar */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-earth/20 backdrop-blur-sm transition-opacity animate-in fade-in duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Content */}
          <div className="absolute top-2 right-2 bottom-2 w-[85%] bg-[#F9F7F2] rounded-[2rem] shadow-2xl p-6 flex flex-col gap-8 animate-in slide-in-from-right duration-300 border border-white/50">
            <div className="flex items-center justify-between pl-2">
              <span className="font-display font-black text-primary text-xl tracking-tight">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="size-10 flex items-center justify-center rounded-full bg-white text-earth shadow-sm hover:text-accent">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <nav className="flex flex-col gap-3">
              {navItems.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleNavClick(item.value)}
                  className={`flex items-center justify-between p-4 rounded-3xl text-lg font-bold transition-all active:scale-95 ${currentPage === item.value
                    ? 'bg-primary text-white shadow-soft'
                    : 'bg-white text-earth border border-sage-light/50'
                    }`}
                >
                  {item.label}
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              ))}
            </nav>

            <div className="mt-auto bg-white p-4 rounded-3xl border border-sage-light/50">
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 px-2">Ngôn ngữ / Language</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { setLanguage('vi'); setIsMobileMenuOpen(false); }}
                  className={`flex-1 py-3 rounded-2xl font-bold border transition-all active:scale-95 ${language === 'vi' ? 'bg-earth text-white border-earth' : 'bg-transparent border-sage-light text-gray-400'}`}
                >
                  Tiếng Việt
                </button>
                <button
                  onClick={() => { setLanguage('en'); setIsMobileMenuOpen(false); }}
                  className={`flex-1 py-3 rounded-2xl font-bold border transition-all active:scale-95 ${language === 'en' ? 'bg-earth text-white border-earth' : 'bg-transparent border-sage-light text-gray-400'}`}
                >
                  English
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default Header;
